import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { between, Device, from, until } from '@helpers/media';
import svgTag from '@img/icons/background/mep-tag.svg?url';
import QuantitySelectorStyles from '@stories/Components/Global/QuantitySelector/QuantitySelector.styles';
import { rgba } from 'polished';
import styled from 'styled-components';
const Container = styled.a `
  --priceColour: ${brand.global.red};
  --tagBgColour: ${brand.global.red};
  --tagFgColour: ${brand.global.white};

  display: flex;
  flex-direction: column;
  text-decoration: none;
  border: 1px solid;
  background-color: ${brand.global.white};
  border-color: var(--borderColour);
  position: relative;
  height: 100%;

  &.focus-ring {
    outline: auto;
    outline-offset: 3px;
    outline-width: 2px;
    outline-style: solid;
    outline-color: ${brand.mep.primary};
  }

  &[data-site='ess'] {
    --borderColour: ${rgba(brand.ess.secondary.darkGrey, 0.15)};
    --purchaseColour: ${brand.ess.primary};
    --textColour: ${brand.ess.primary};

    &[data-contains-tag='true'] {
      border-top: 1px solid var(--tagBgColour);
    }
  }

  &[data-site='mep'] {
    --borderColour: ${rgba(brand.mep.secondary.darkestGrey, 0.15)};
    --purchaseColour: ${brand.mep.primary};
    --textColour: ${brand.mep.primary};

    overflow: hidden;
    border-radius: 8px;

    &[data-variant='combination'] {
      @media ${until(Device.Tablet)} {
        border-radius: 0;
      }
    }
  }

  &[data-variant='listing'] {
    /* Mobile click styles - card should mimic press */
    @media ${until(Device.TabletLarge)} {
      ${transition('transform', 0.15)};

      &:active {
        transform: translateY(1.5px);
      }
    }
  }

  &[data-variant='combination'] {
    @media ${until(Device.Tablet)} {
      border-top: 0 !important;
    }
  }
`;
const Tag = styled.div `
  ${fonts.daxWide.bold};

  position: absolute;
  top: 0;
  right: 0;
  background-color: var(--tagBgColour);
  padding: ${fluid(8, 12)} ${fluid(18, 22)};
  font-size: ${fluid(14, 18)};
  line-height: ${fluid(20, 26)};
  color: var(--tagFgColour);
  text-transform: uppercase;
  z-index: 1;

  ${Container}[data-site='mep'] & {
    padding: 6px 10px 37px 34px;
    background-color: transparent;
    background-image: url(${svgTag});
    background-size: 100% 100%;
    background-position: top right;
  }

  ${Container}[data-variant='combination'] & {
    @media ${until(Device.Tablet)} {
      display: none;
    }
  }
`;
const Inner = styled.div `
  padding: ${fluid(8, 20)};
  color: var(--textColour);
`;
const ImageWrapper = styled.div `
  ${imageWrapperPreserveRatio(1, 1)};

  ${Container}[data-site='ess'] & {
    margin: 0 0 8px;
  }

  ${Container}[data-site='mep'] & {
    margin: 0 0 4px;
  }

  ${Container}[data-variant='combination'] & {
    @media ${until(Device.Tablet)} {
      display: none;
    }
  }
`;
const Image = styled.img `
  ${imageCoverContainer()};
  ${transition('transform', 0.6)};
  object-fit: contain;

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-variant='listing']:hover &,
    ${Container}.focus-ring & {
      transform: scale(1.05);
    }
  }
`;
const Name = styled.h3 `
  ${fonts.daxWide.bold};

  font-size: ${fluid(14, 18)};
  line-height: ${fluid(20, 26)};
  margin: 0 0 ${fluid(8, 8)};
  max-width: 22ch;
  color: inherit;

  ${Container}[data-site='mep'] & {
    letter-spacing: 0.025em;
  }

  @media ${from(Device.TabletLarge)} {
    max-width: 22ch;
  }
`;
const Sku = styled.span `
  ${fonts.daxWide.regular};

  display: block;
  font-size: ${fluid(14, 18)};
  line-height: ${fluid(20, 26)};
  letter-spacing: 0.025em;

  ${Container}[data-site='ess'] & {
    color: ${brand.ess.secondary.darkGrey};
  }

  ${Container}[data-site='mep'] & {
    color: ${brand.ess.secondary.darkestGrey};
  }
`;
const PriceWrapper = styled.div `
  margin: 0 0 6px;
  color: inherit;

  @media ${from(Device.TabletLarge)} {
    margin: 0;
  }
`;
const Price = styled.span `
  font-size: ${fluid(20, 24)};
  line-height: ${fluid(28, 32)};

  ${Container}[data-site='ess'] & {
    ${fonts.daxWide.bold};
  }

  ${Container}[data-site='mep'] & {
    ${fonts.museoSlab.bold};
  }

  @media ${between(Device.TabletLarge, Device.DesktopLarge)} {
    font-size: 18px;
  }
`;
const Suffix = styled.span `
  ${fonts.daxWide.regular};

  font-size: 14px;
  letter-spacing: 0.025em;

  @media ${between(Device.TabletLarge, Device.ActualDesktop)} {
    font-size: 12px;
  }

  ${PriceWrapper}[data-mobile-label='true'] & {
    &:nth-child(2) {
      display: none;

      @media ${from(Device.MobileLarge)} {
        display: inline-block;
      }
    }

    &:nth-child(3) {
      @media ${from(Device.MobileLarge)} {
        display: none;
      }
    }
  }
`;
const ActionWrapper = styled.span `
  ${transition('text-decoration-color', 0.2)};
  ${fonts.daxWide.bold};

  font-size: ${fluid(16, 18, Device.DesktopLarge)};
  line-height: 26px;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: underline;
  text-decoration-color: currentColor;
  text-decoration-thickness: 1px;
  text-underline-offset: 4px;

  @media ${from(Device.TabletLarge)} {
    text-decoration-color: transparent;
    line-height: 22px;

    ${Container}[data-variant='listing']:hover &,
    ${Container}.focus-ring & {
      text-decoration-color: currentColor;
    }
  }

  @media ${between(Device.TabletLarge, Device.DesktopLarge)} {
    font-size: 14px;
  }
`;
const IconWrapper = styled.div `
  display: block;
  color: currentColor;
  height: 14px;
  margin: 0;
  padding: 0 0 0 6px;
  /* Ensure icon sits on top of MEP overlay */
  position: relative;

  > svg,
  > img {
    ${transition('transform', 0.2)};

    height: 14px;
    display: block;

    @media ${from(Device.TabletLarge)} {
      ${Container}[data-variant='listing']:hover &,
      ${Container}.focus-ring & {
        transform: translateX(15%);
      }
    }

    @media ${between(Device.TabletLarge, Device.DesktopLarge)} {
      height: 12px;
    }
  }
`;
const FakeButton = styled.div `
  background-color: ${brand.global.red};
  color: ${brand.global.white};
  padding: 8px ${fluid(12, 22)};
  text-transform: uppercase;
  text-align: center;
  letter-spacing: 0.025em;

  &[data-product-type='purchase'] {
    background-color: var(--purchaseColour);
  }

  ${Container}[data-site='mep'] & {
    padding-top: 13px;
    padding-bottom: 13px;
  }

  a& {
    display: block;
    text-decoration: none;
  }

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-site='ess'][data-variant='listing'] & {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
    }

    a& {
      &.focus-ring,
      &:hover {
        ${ActionWrapper} {
          text-decoration-color: currentColor;
        }
        ${IconWrapper} {
          transform: translateX(15%);
        }
      }
    }
  }
`;
const BottomWrapper = styled.div `
  margin-top: auto;

  ${QuantitySelectorStyles.Container} {
    margin: 0 ${fluid(8, 20)} ${fluid(8, 20)};

    ${Container}[data-site='mep'] & {
      --bgColour: ${brand.mep.secondary.lightestGrey};
    }
  }

  > ${PriceWrapper} {
    padding: 0 ${fluid(8, 20)} ${fluid(8, 20)};
    margin: 0;
    color: var(--priceColour);
  }

  @media ${until(Device.Tablet)} {
    ${Container}[data-variant='combination'] & {
      display: grid;
      grid-template-columns: 1fr 1fr;

      ${QuantitySelectorStyles.Container} {
        grid-row: 1 / span 2;
      }

      > ${PriceWrapper} {
        padding-top: 0;
      }

      ${FakeButton} {
        margin: 0 ${fluid(8, 20)} ${fluid(8, 20)} 0;
        padding-top: 8px;
        padding-bottom: 8px;
        grid-column: 2 / span 1;
      }
    }

    ${Container}[data-variant='combination'][data-site='mep'] & {
      ${FakeButton} {
        border-radius: 8px;
      }
    }
  }

  /* Mobile click styles - card should mimic press */
  @media ${until(Device.TabletLarge)} {
    ${Container}[data-variant='combination'] & {
      ${FakeButton} {
        ${transition('transform', 0.15)};

        &:active {
          transform: translateY(1.5px);
        }
      }
    }
  }
`;
const ProductCardStyles = {
    ActionWrapper,
    BottomWrapper,
    Container,
    FakeButton,
    IconWrapper,
    Image,
    ImageWrapper,
    Inner,
    Name,
    Price,
    PriceWrapper,
    Sku,
    Suffix,
    Tag,
};
export default ProductCardStyles;
